import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {concatLatestFrom} from '@ngrx/operators';
import {Action, Store} from '@ngrx/store';
import {forkJoin, from, Observable, of, timer} from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {SeFeToastService} from 'se-fe-toast';
import * as addOnActions from '../../add-ons/add-on.actions';
import {selectCartItemFromCurrentProduct} from '../../add-ons/reducers';
import {CartApiService, CartItemApiService} from '../../data/api/service';
import {CartItem} from '../../data/model';
import * as contextSelector from '../context-data/context-data.selectors';
import {CommerceError} from '../error-handling/commerce-error';
import {
  logError,
  silentOidcAttemptError,
  unrecoverableError,
} from '../error-handling/error-handling.actions';
import * as eventActions from '../event-emitter/event.actions';
import * as cartActions from './cart.actions';
import {selectCartItems, selectFailedAddons} from './cart.selectors';
import {EventEmitterService} from '../event-emitter/event-emitter.service';

@Injectable()
export class CartEffects {
  addClareBundleToCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.addClareBundleToCart),
      concatLatestFrom(() => this.store.select(selectCartItems)),
      concatMap(([action, cartItems]) => {
        const actionItems = (action.items ?? []) as CartItem[];
        if (actionItems.length === 0) {
          this.toast.critical(
            `Failed to add ${action.bundleDisplayName} to your cart.`,
          );
          return of(
            cartActions.cartActionFailure({
              error: {
                error: new Error('No items in bundle'),
                message: 'There was a problem adding the bundle to the cart.',
                code: 522,
              },
            }),
          );
        }

        const firstItem = actionItems[0];
        const bundleInstanceId = firstItem?.bundle_instance_id ?? '';

        const existingBundleItems = cartItems.filter(
          item =>
            item.bundle_instance_id &&
            item.bundle_instance_id === bundleInstanceId,
        );

        if (existingBundleItems.length > 0) {
          // Increment quantity for each item in the bundle in parallel
          const newQuantity = (existingBundleItems[0].quantity ?? 1) + 1;
          return this.bundleQuantityEditRequests(
            existingBundleItems,
            newQuantity,
          ).pipe(
            tap(() => {
              this.toast.done(
                `${action.bundleDisplayName} quantity was updated in your cart.`,
              );
            }),
            switchMap(() =>
              of(cartActions.loadCart(), eventActions.cartUpdate()),
            ),
            catchError(err =>
              this.onBundleQuantityUpdateFailure(action.bundleDisplayName, err),
            ),
          );
        }

        return forkJoin(
          actionItems.map(item => this.cartItemApi.add(item)),
        ).pipe(
          tap(() => {
            this.toast.done(
              `${action.bundleDisplayName} was added to your cart.`,
            );
          }),
          switchMap(() =>
            of(cartActions.loadCart(), eventActions.cartUpdate()),
          ),
          catchError(err => {
            this.toast.critical(
              `Failed to add ${action.bundleDisplayName} to your cart.`,
            );
            return of(
              cartActions.cartActionFailure({
                error: {
                  error: err,
                  message: 'There was a problem adding the bundle to the cart.',
                  code: 522,
                },
              }),
            );
          }),
        );
      }),
    );
  });

  updateBundleItemsQuantity$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.updateBundleItemsQuantity),
      switchMap(action =>
        this.bundleQuantityEditRequests(action.items, action.quantity).pipe(
          switchMap(updatedItems => {
            this.toast.done(
              `${action.bundleDisplayName} was updated in your cart.`,
            );
            return of(
              cartActions.updateBundleItemsQuantitySuccess({
                items: updatedItems,
              }),
              eventActions.cartUpdate(),
            );
          }),
          catchError((error: unknown) =>
            this.onBundleQuantityUpdateFailure(action.bundleDisplayName, error),
          ),
        ),
      ),
    );
  });

  removeBundleItemsFromCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.removeBundleItemsFromCart),
      concatMap(action =>
        this.cartItemApi
          .removeBatch(action.items.map(item => String(item.id)))
          .pipe(
            tap(() => {
              this.toast.done(
                `${action.bundleDisplayName} was removed from your cart.`,
              );
            }),
            switchMap(() =>
              of(cartActions.loadCart(), eventActions.cartUpdate()),
            ),
            catchError(error => {
              this.toast.critical(
                `Failed to remove ${action.bundleDisplayName} from your cart.`,
              );
              return of(
                cartActions.cartActionFailure({
                  error: {
                    error,
                    message: 'There was a problem removing the bundle.',
                    code: 521,
                  },
                }),
              );
            }),
          ),
      ),
    );
  });

  addOrUpdateCartItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.addOrUpdateCartItem),
      concatLatestFrom(() => this.store.select(selectCartItems)),
      map(([action, addons]) => {
        const itemIndex = addons.findIndex(a => a.equals(action.item));
        const itemInCart = itemIndex > -1;

        const item = itemInCart ? new CartItem(addons[itemIndex]) : action.item;
        if (
          itemInCart &&
          (item.maxQuantity === null ||
            item.quantity < (item.maxQuantity ?? Infinity))
        ) {
          item.quantity += action.item.quantity ?? 1;
        }

        if (itemInCart) {
          return action.options
            ? cartActions.updateCartItem({item, options: action.options})
            : cartActions.updateCartItem({item});
        }

        return action.options
          ? cartActions.addItemToCart({item, options: action.options})
          : cartActions.addItemToCart({item});
      }),
    );
  });

  addItemToCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        cartActions.addItemToCart,
        cartActions.addItemToCartAndContinue,
        cartActions.addItemToCartAndComplete,
        cartActions.addItemToCartAndRegisterAnother,
        cartActions.addItemToCartAndContinueToShipping,
      ),
      concatMap(action =>
        this.cartItemApi.add(action.item).pipe(
          switchMap((item: CartItem) => {
            const type = action.type.valueOf();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let mapToAction: Action<any>;
            switch (type) {
              case cartActions.addItemToCartAndContinue.type:
                mapToAction = cartActions.addItemToCartSuccessThenContinue({
                  item,
                });
                break;
              case cartActions.addItemToCartAndComplete.type:
                mapToAction = cartActions.addItemToCartSuccessThenComplete({
                  item,
                });
                break;
              case cartActions.addItemToCartAndRegisterAnother.type:
                mapToAction =
                  cartActions.addItemToCartSuccessAndRegisterAnother({
                    item,
                  });
                break;
              case cartActions.addItemToCartAndContinueToShipping.type:
                mapToAction =
                  cartActions.addItemToCartSuccessAndContinueToShipping({item});
                break;
              default:
                mapToAction = cartActions.addItemToCartSuccess({item});
                break;
            }
            // Show toast for the item the user requested, not the returned item
            const requestedItem = action.item;
            const isShippingItem =
              ['shipping', 'shipping-fee'].includes(
                requestedItem.templateKey,
              ) ||
              requestedItem.fulfilledBy === 'shipping' ||
              requestedItem.name?.toLowerCase().includes('shipping') ||
              requestedItem.name?.toLowerCase().includes('shipping fee');
            if (!isShippingItem) {
              this.toast.done(`${requestedItem.name} was added to your cart.`);
            }
            const actions = [mapToAction, eventActions.cartUpdate()];
            if (action.options?.sync) {
              actions.push(cartActions.loadCart());
            }
            return of(...actions);
          }),
          catchError(err => {
            this.toast.critical(
              `Failed to add ${action.item.name} to your cart.`,
            );
            return of(
              cartActions.addItemToCartFailure({item: action.item, error: err}),
            );
          }),
        ),
      ),
    );
  });

  retryCartItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.retryAddCartItems),
      mergeMap(action =>
        from(action.cartItems).pipe(
          concatMap(item =>
            this.cartItemApi.add(item).pipe(
              map(item => cartActions.retryCartItemSuccess({item: item})),
              catchError(() => {
                this.toast.critical(`Retry failed for ${item.name}.`);
                return of(cartActions.retryCartItemFailure({item}));
              }),
            ),
          ),
        ),
      ),
    );
  });

  retryCartItemSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(cartActions.retryCartItemSuccess),
        withLatestFrom(this.store.select(selectFailedAddons)),
        tap(([action, failedAddons]) => {
          this.toast.done(`${action.item.name} was added to your cart.`);
          if (failedAddons.length === 0) {
            this.router.navigate([
              {outlets: {commerceElement: 'commerce/address'}},
            ]);
          }
        }),
      );
    },
    {dispatch: false},
  );

  navigateToFailedAddons$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(cartActions.navToFailedAddons),
        tap(() => {
          this.router.navigate([
            {outlets: {commerceElement: 'commerce/failed-add-ons'}},
          ]);
        }),
      );
    },
    {dispatch: false},
  );

  addItemToCartFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.addItemToCartFailure),
      map(action =>
        cartActions.cartActionFailure({
          error: {
            error: action.error,
            message: 'There was a problem adding the item to the cart.',
            code: 522,
          },
        }),
      ),
    );
  });

  addItemToCartSuccessAndRegisterAnother = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.addItemToCartSuccessAndRegisterAnother),
      map(cartActions.registerAnotherPerson),
    );
  });

  addItemToCartSuccessThenContinue$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        cartActions.addItemToCartSuccessThenContinue,
        cartActions.removeItemFromCartSuccessThenContinue,
      ),
      switchMap(() => timer(500).pipe(map(() => addOnActions.nextAddOn()))),
    );
  });

  addItemToCartSuccessAndContinueToShipping$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          cartActions.addItemToCartSuccessAndContinueToShipping,
          cartActions.removeItemFromCartSuccessThenShipping,
        ),
        tap(() => {
          this.router.navigate([{outlets: {commerceElement: 'shipping-form'}}]);
        }),
      );
    },
    {dispatch: false},
  );

  addItemToCartSuccessThenComplete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        cartActions.addItemToCartSuccessThenComplete,
        cartActions.removeItemFromCartSuccessThenComplete,
      ),
      map(cartActions.completeOrder),
    );
  });

  updateCartItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.updateCartItem),
      switchMap(action =>
        this.cartItemApi.edit(action.item).pipe(
          switchMap(cartItem => {
            const actions = [];
            actions.push(cartActions.updateCartItemSuccess({item: cartItem}));
            actions.push(eventActions.cartUpdate());
            if (action.options?.sync) {
              actions.push(cartActions.loadCart());
            }
            this.toast.done(`${action.item.name} was updated in your cart.`);

            return of(...actions);
          }),
          catchError(error =>
            of(
              cartActions.cartActionFailure({
                error: {
                  error: error,
                  message: 'There was a problem updating the cart item.',
                  code: 520,
                },
              }),
            ),
          ),
        ),
      ),
    );
  });

  cartActionFailed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.cartActionFailure, cartActions.cartSubmitFailure),
      tap(error => {
        this.eventEmitterService.addOnError({
          message: error.error.message,
          code: error.error?.code || 500,
          data: {...error},
        });
      }),
      map(logError),
    );
  });

  /**
   * Make a request to CommerceAPI with instruction to submit the cart to clare
   */
  completeOrder$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.completeOrder),
      map(() => cartActions.completeOrderSuccess()),
    );
  });

  /**
   * Emit an event to the host application with instruction to continue
   */
  completeOrderSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.completeOrderSuccess),
      map(() => {
        return eventActions.continueToCheckout();
      }),
    );
  });

  /**
   * Emit an event to the host application with instruction to register another person
   */
  registerAnotherPerson$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.registerAnotherPerson),
      map(() => {
        return eventActions.registerAnother();
      }),
    );
  });

  removeItemFromCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        cartActions.removeItemFromCart,
        cartActions.removeItemFromCartThenContinue,
        cartActions.removeItemFromCartThenComplete,
        cartActions.removeItemFromCartThenRegisterAnother,
        cartActions.removeItemFromCartThenShipping,
      ),
      concatMap(action =>
        this.cartItemApi.remove(action.item.id!).pipe(
          concatLatestFrom(() => this.store.select(selectCartItems)),
          switchMap(([, items]) => {
            const item = new CartItem(action.item);
            const type = action.type;
            let mapToAction: Action<string>;
            switch (type) {
              case cartActions.removeItemFromCartThenContinue.type:
                mapToAction = cartActions.removeItemFromCartSuccessThenContinue(
                  {item},
                );
                break;
              case cartActions.removeItemFromCartThenComplete.type:
                mapToAction = cartActions.removeItemFromCartSuccessThenComplete(
                  {item},
                );
                break;
              case cartActions.removeItemFromCartThenShipping.type:
                mapToAction = cartActions.removeItemFromCartSuccessThenShipping(
                  {item},
                );
                break;
              case cartActions.removeItemFromCartThenRegisterAnother.type:
                mapToAction =
                  cartActions.removeItemFromCartThenRegisterAnotherSuccess({
                    item,
                  });
                break;
              default:
                mapToAction = cartActions.removeItemFromCartSuccess({item});
                break;
            }

            this.toast.done(`${item.name} was removed from your cart.`);

            const itemsAfterRemoval = (items || []).filter(
              i => i.id !== item.id,
            );
            const hasTaxableItemsAfter = itemsAfterRemoval.some(
              i => i.templateKey === 'yso-products',
            );

            const actionsToDispatch: Action<string>[] = [
              mapToAction,
              eventActions.cartUpdate(),
            ];

            if (!hasTaxableItemsAfter) {
              actionsToDispatch.push(cartActions.getCartTax());
            }

            return of(...actionsToDispatch);
          }),
          catchError(error => {
            this.toast.critical(
              `Failed to remove ${action.item.name} from your cart.`,
            );
            return of(
              cartActions.cartActionFailure({
                error: {
                  error: error,
                  message:
                    'There was a problem removing the item from the cart.',
                  code: 521,
                },
              }),
            );
          }),
        ),
      ),
    );
  });

  removeCurrentItemFromCartThenComplete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.removeCurrentAddonFromCartThenComplete),
      concatLatestFrom(() => [
        this.store.select(selectCartItemFromCurrentProduct),
      ]),
      filter(([, cartItem]) => Boolean(cartItem)),
      map(([, cartItem]) =>
        cartActions.removeItemFromCartThenComplete({item: cartItem!}),
      ),
    );
  });

  removeCurrentItemFromCartThenContinue$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.removeCurrentAddonFromCartThenContinue),
      concatLatestFrom(() =>
        this.store.select(selectCartItemFromCurrentProduct),
      ),
      filter(([, cartItem]) => Boolean(cartItem)),
      map(([, cartItem]) => {
        return cartActions.removeItemFromCartThenContinue({item: cartItem!});
      }),
    );
  });

  removeCurrentItemFromCartThenShipping$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.removeCurrentAddonFromCartThenShipping),
      concatLatestFrom(() =>
        this.store.select(selectCartItemFromCurrentProduct),
      ),
      filter(([, cartItem]) => Boolean(cartItem)),
      map(([, cartItem]) => {
        return cartActions.removeItemFromCartThenShipping({item: cartItem!});
      }),
    );
  });

  loadCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.loadCart),
      concatLatestFrom(() =>
        this.store.select(contextSelector.selectContextData),
      ),
      switchMap(([__action, context]) => {
        if (context) {
          return this.cartItemApi.list(context).pipe(
            switchMap(cartItems =>
              of(
                cartActions.loadCartSuccess({cartItems}),
                eventActions.cartLoad(),
              ),
            ),
            catchError(error => {
              if (error?.error?.message === 'Invalid Token') {
                return of(
                  silentOidcAttemptError({
                    error: new CommerceError('Missing token', error.stack, 401),
                  }),
                );
              } else {
                return of(
                  cartActions.loadCartFailure({
                    error: new CommerceError(
                      'Unable to load shopping cart.',
                      error.stack,
                      503,
                    ),
                  }),
                );
              }
            }),
          );
        } else {
          return of(
            cartActions.loadCartFailure({
              error: new CommerceError(
                'Context data is undefined. Unable to load shopping cart.',
                undefined,
                404,
              ),
            }),
          );
        }
      }),
    );
  });

  /**
   * Reload cart when cart slide opens to ensure it's in sync with the backend.
   * This fixes issues where items added from different catalogs may not display properly.
   */
  showCartSlideOut$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.showCartSlideOut),
      concatLatestFrom(() => this.store.select(selectCartItems)),
      filter(([, items]) => items.length === 0),
      map(() => cartActions.loadCart()),
    );
  });

  loadCartFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.loadCartFailure),
      map(({error}) => unrecoverableError({error})),
    );
  });

  removeItemFromCartThenRegisterAnotherSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        cartActions.removeItemFromCartThenRegisterAnotherSuccess,
        cartActions.addItemToCartSuccessAndRegisterAnother,
      ),
      map(() => eventActions.registerAnother()),
    );
  });

  getCartTaxEstimation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.getCartTax),
      switchMap(() =>
        this.cartApi.getTaxEstimate().pipe(
          map(tax => cartActions.getCartTaxSuccess({tax})),
          catchError(error => of(cartActions.getCartTaxFailure({error}))),
        ),
      ),
    );
  });

  emptyCart$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(cartActions.emptyCart),
      switchMap(() => {
        return this.cartApi.emptyCart().pipe(
          map(() => {
            this.toast.success('Your cart has been emptied.');
            return cartActions.emptyCartSuccess();
          }),
          catchError(error => {
            this.toast.error('Failed to empty your cart.');
            return of(
              cartActions.emptyCartFailure({
                error: {
                  error,
                  message: 'Failed to empty your cart.',
                  code: 500,
                },
              }),
            );
          }),
        );
      }),
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store,
    private toast: SeFeToastService,
    private router: Router,
    private eventEmitterService: EventEmitterService,
    private cartItemApi: CartItemApiService,
    private cartApi: CartApiService,
  ) {}

  /**
   * Runs one cart line edit per bundle child for a quantity change.
   */
  private bundleQuantityEditRequests(
    items: CartItem[],
    quantity: number,
  ): Observable<CartItem[]> {
    if (items.length === 0) return of([]);

    return forkJoin(
      items.map(item =>
        this.cartItemApi.edit(
          new CartItem({
            ...item,
            quantity,
          }),
        ),
      ),
    );
  }

  /**
   * Surfaces failure and reloads cart state: partial line updates may have
   * succeeded before another request failed, so we re-sync from the server.
   */
  private onBundleQuantityUpdateFailure(
    bundleDisplayName: string,
    error: unknown,
  ): Observable<Action> {
    this.toast.critical(`Failed to update ${bundleDisplayName} quantity.`);
    return of(
      cartActions.cartActionFailure({
        error: {
          error,
          message: 'There was a problem updating the bundle quantity.',
          code: 520,
        },
      }),
      cartActions.loadCart(),
      eventActions.cartUpdate(),
    );
  }
}
